import AssessmentIcon from '@material-ui/icons/Assessment';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ReceiptIcon from '@material-ui/icons/Receipt';
import React from 'react';
import styled from 'styled-components';
import { TipoRelatorio } from '../../../interface';

const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin: 0 0 1rem 0;
  width: 100%;
`;

const SectionLabel = styled.p`
  width: 100%;
  font-size: ${(props) => props.theme.fontSize.small};
  color: ${(props) => props.theme.colors.lightGray};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 -0.25rem 0;
`;

interface CardProps {
  active: boolean;
}

const Card = styled.button<CardProps>`
  flex: 1 1 18rem;
  position: relative;
  text-align: left;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-radius: ${(props) => props.theme.border.default};
  background-color: ${(props) => props.theme.colors.white};
  border: 2px solid
    ${(props) => (props.active ? props.theme.colors.mainColor : 'transparent')};
  box-shadow: ${(props) =>
    props.active
      ? `0 0 0 1px ${props.theme.colors.mainColor}, ${props.theme.shadow.default}`
      : props.theme.shadow.default};
  cursor: pointer;
  transition: all ${(props) => props.theme.transitionDuration.default};

  &:hover {
    border-color: ${(props) => props.theme.colors.emphasis};
  }
`;

const IconBadge = styled.div<CardProps>`
  flex-shrink: 0;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: ${(props) => props.theme.border.default};
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) =>
    props.active
      ? props.theme.colors.mainColor
      : props.theme.colors.background};
  color: ${(props) =>
    props.active ? props.theme.colors.white : props.theme.colors.lightGray};
  transition: all ${(props) => props.theme.transitionDuration.default};

  & svg {
    font-size: 1.5rem;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
`;

const CardTitle = styled.span<CardProps>`
  font-size: ${(props) => props.theme.fontSize.medium};
  font-weight: 700;
  color: ${(props) =>
    props.active ? props.theme.colors.mainColor : props.theme.colors.gray};
`;

const Badge = styled.span`
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0.1rem 0.45rem;
  border-radius: 1rem;
  background-color: ${(props) => props.theme.colors.accent};
  color: ${(props) => props.theme.colors.mainColor};
`;

const CardDescription = styled.span`
  font-size: ${(props) => props.theme.fontSize.small};
  color: ${(props) => props.theme.colors.lightGray};
  line-height: 1.35;
`;

const ActiveCheck = styled(CheckCircleIcon)`
  position: absolute;
  top: 0.6rem;
  right: 0.6rem;
  color: ${(props) => props.theme.colors.mainColor};
  font-size: 1.2rem !important;
`;

interface Props {
  value: TipoRelatorio;
  // eslint-disable-next-line no-unused-vars
  onChange: (tipoRelatorio: TipoRelatorio) => void;
}

interface OptionConfig {
  tipo: TipoRelatorio;
  titulo: string;
  descricao: string;
  badge?: string;
  Icon: typeof AssessmentIcon;
}

// Tarifas Gerais aparece primeiro porque e o relatorio principal (substitui
// o menu>Tarifas legado). Recebimento fica como opcao secundaria.
const OPTIONS: OptionConfig[] = [
  {
    tipo: 'tarifas',
    titulo: 'Tarifas Gerais',
    descricao:
      'Todas as tarifas do parceiro no período: TED, PIX, Boleto, Mensalidade, Cartão, Cripto e mais — em um único relatório.',
    badge: 'Novo',
    Icon: AssessmentIcon,
  },
  {
    tipo: 'recebimento',
    titulo: 'Tarifas de Recebimento',
    descricao:
      'Apenas as tarifas cobradas no recebimento de PIX, TED, TEV, Boleto e Domicílio.',
    Icon: ReceiptIcon,
  },
];

function TypeSelector({ value, onChange }: Props) {
  return (
    <Wrapper>
      <SectionLabel>Escolha o tipo de relatório</SectionLabel>
      {OPTIONS.map((opt) => {
        const active = value === opt.tipo;
        return (
          <Card
            key={opt.tipo}
            type="button"
            active={active}
            onClick={() => onChange(opt.tipo)}>
            {active && <ActiveCheck />}
            <IconBadge active={active}>
              <opt.Icon />
            </IconBadge>
            <Content>
              <TitleRow>
                <CardTitle active={active}>{opt.titulo}</CardTitle>
                {opt.badge && <Badge>{opt.badge}</Badge>}
              </TitleRow>
              <CardDescription>{opt.descricao}</CardDescription>
            </Content>
          </Card>
        );
      })}
    </Wrapper>
  );
}

export default TypeSelector;
